import { getRecord, putRecord } from './db';
import { getDeviceId } from './device';
import { currentOfflineAuthorizationEnvelope } from './authEnvelope';

export interface WorkflowCheckpoint<T = unknown> {
  workflow_id: string;
  tenant_id: string;
  user_id: string;
  device_id: string;
  workflow_type: string;
  state: string;
  step: string | null;
  last_local_sequence: number | null;
  last_user_action: string | null;
  draft_data: T | null;
  accepted_data: unknown;
  sync_state: string;
  created_at: string;
  updated_at: string;
}

export function workflowId(type: string, userId: string): string {
  return `${type}:${userId}:${getDeviceId()}`;
}

export async function saveWorkflowCheckpoint<T>(userId: string, type: string, patch: Partial<WorkflowCheckpoint<T>>): Promise<WorkflowCheckpoint<T>> {
  const id = workflowId(type, userId);
  const existing = await getRecord<WorkflowCheckpoint<T>>('workflow_checkpoints', id);
  const envelope = await currentOfflineAuthorizationEnvelope(userId);
  if (!envelope) throw new Error('Cannot checkpoint workflow without a current device authorization envelope');
  const now = new Date().toISOString();
  const checkpoint: WorkflowCheckpoint<T> = {
    workflow_id: id,
    tenant_id: envelope.tenant_id,
    user_id: userId,
    device_id: getDeviceId(),
    workflow_type: type,
    state: patch.state || existing?.state || 'ACTIVE',
    step: patch.step ?? existing?.step ?? null,
    last_local_sequence: patch.last_local_sequence ?? existing?.last_local_sequence ?? null,
    last_user_action: patch.last_user_action ?? existing?.last_user_action ?? null,
    draft_data: patch.draft_data ?? existing?.draft_data ?? null,
    accepted_data: patch.accepted_data ?? existing?.accepted_data ?? null,
    sync_state: patch.sync_state || existing?.sync_state || 'LOCAL_ONLY',
    created_at: existing?.created_at || now,
    updated_at: now,
  };
  await putRecord('workflow_checkpoints', checkpoint);
  return checkpoint;
}

export async function loadWorkflowCheckpoint<T>(userId: string, type: string): Promise<WorkflowCheckpoint<T> | null> {
  return (await getRecord<WorkflowCheckpoint<T>>('workflow_checkpoints', workflowId(type, userId))) || null;
}
